import type { InstalledPlugin, MachineSkillGroup } from '@crowdex/plugin-contracts';
import { Badge } from '@crowdex/ui/components/badge';
import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Switch } from '@crowdex/ui/components/switch';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronRight, LoaderCircle } from 'lucide-react';
import { useId, useState } from 'react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { PluginLogo } from './plugin-logo';

type MachineSkillPlugin = InstalledPlugin & { machineSkills: MachineSkillGroup };

export function isMachineSkillGroup(plugin: InstalledPlugin): plugin is MachineSkillPlugin {
  return plugin.machineSkills !== undefined;
}

/** A group's skills, its own first, then those an installed plugin provides too. */
function machineGroupSkills(plugin: MachineSkillPlugin) {
  const alsoIn = new Map((plugin.machineSkills.alsoIn ?? []).map((entry) => [entry.skill, entry]));
  const all = [...plugin.skills, ...(plugin.inactiveContributions?.skills ?? [])];
  return {
    own: all.filter((skill) => !alsoIn.has(skill.name)),
    duplicates: all.flatMap((skill) => {
      const entry = alsoIn.get(skill.name);
      return entry === undefined ? [] : [{ skill, plugin: entry.plugin }];
    }),
  };
}

/** `heygen-com/hyperframes · 5 skills · 5 also in Video`. */
function machineGroupLine(plugin: MachineSkillPlugin): string {
  const { own, duplicates } = machineGroupSkills(plugin);
  const providers = [...new Set(duplicates.map((entry) => entry.plugin))];
  return [
    plugin.machineSkills.source,
    `${own.length} ${own.length === 1 ? 'skill' : 'skills'}`,
    duplicates.length > 0 ? `${duplicates.length} also in ${providers.join(', ')}` : null,
    plugin.enabled ? null : 'not used in Crowdex',
  ]
    .filter(Boolean)
    .join(' · ');
}

function folderLabel(group: MachineSkillGroup, folder: string): string {
  return `${group.label}/${folder.split(/[\\/]/).at(-1) ?? folder}`;
}

/** The skills of this computer by root, each source one row (D31: "On this computer"). */
export function MachineSkillsSections({
  groups,
  busyPlugin,
  phone,
  removable,
  onToggle,
  onOpen,
  onRemove,
}: {
  groups: readonly InstalledPlugin[];
  busyPlugin: string | null;
  phone: boolean;
  /** Whether "Remove from this computer…" is offered (a Crowdex of this computer). */
  removable: boolean;
  onToggle: (plugin: InstalledPlugin) => void;
  onOpen: (plugin: InstalledPlugin) => void;
  onRemove: (plugin: InstalledPlugin) => Promise<void>;
}) {
  const roots = new Map<string, { label: string; shared: boolean; groups: MachineSkillPlugin[] }>();
  for (const plugin of groups) {
    if (!isMachineSkillGroup(plugin)) continue;
    const entry = roots.get(plugin.machineSkills.root) ?? {
      label: plugin.machineSkills.label,
      shared: plugin.machineSkills.shared,
      groups: [],
    };
    entry.groups.push(plugin);
    roots.set(plugin.machineSkills.root, entry);
  }
  return (
    <>
      {[...roots.entries()].map(([root, entry]) => (
        <section aria-label={`On this computer · ${entry.label}`} className="mt-6" key={root}>
          <p className="px-1 text-muted-foreground text-sm">
            On this computer · <span className="font-mono text-xs">{entry.label}</span>
          </p>
          <p className="mt-0.5 mb-2 px-1 text-muted-foreground text-xs">
            {entry.shared
              ? 'Other agents on this computer, like Codex, read these skills too. '
              : 'Every Crowdex on this computer reads these skills. '}
            The switch only decides whether Crowdex’s agents use them here.
          </p>
          <div className="overflow-hidden rounded-2xl border bg-card">
            {entry.groups.map((plugin) => (
              <MachineSkillGroupRow
                busy={busyPlugin === plugin.name}
                key={plugin.name}
                onOpen={onOpen}
                onRemove={onRemove}
                onToggle={onToggle}
                phone={phone}
                plugin={plugin}
                removable={removable}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/** One source of this computer's skills: its switch, and its skills when expanded. */
function MachineSkillGroupRow({
  plugin,
  busy,
  phone,
  removable,
  onToggle,
  onOpen,
  onRemove,
}: {
  plugin: MachineSkillPlugin;
  busy: boolean;
  phone: boolean;
  removable: boolean;
  onToggle: (plugin: InstalledPlugin) => void;
  onOpen: (plugin: InstalledPlugin) => void;
  onRemove: (plugin: InstalledPlugin) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const listId = useId();
  const { own, duplicates } = machineGroupSkills(plugin);
  return (
    <div className="border-hairline border-b last:border-b-0" data-machine-skills={plugin.name}>
      <div className={cn('flex items-center gap-3 px-3.5 py-2.5', phone && 'px-4 py-3')}>
        <button
          aria-controls={listId}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} the skills of ${plugin.displayName}`}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          onClick={() => setExpanded((value) => !value)}
          type="button"
        >
          <span className={cn('shrink-0', !plugin.enabled && 'opacity-50')} data-plugin-logo>
            <PluginLogo plugin={plugin} size="small" />
          </span>
          <span className="min-w-0 flex-1">
            <span
              className={cn(
                'block truncate',
                phone ? 'text-base' : 'text-sm',
                !plugin.enabled && 'text-muted-foreground',
              )}
            >
              {plugin.displayName}
            </span>
            <span className="block truncate text-muted-foreground text-xs">
              {machineGroupLine(plugin)}
            </span>
          </span>
          <ChevronRight
            aria-hidden
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
          />
        </button>
        {busy && <LoaderCircle className="size-3.5 animate-spin text-muted-foreground" />}
        <div className="flex shrink-0 items-center gap-2 text-muted-foreground text-xs">
          {!phone && <span aria-hidden>Use in Crowdex</span>}
          <Switch
            aria-label={`Use ${plugin.displayName} in Crowdex`}
            checked={plugin.enabled}
            disabled={busy}
            onCheckedChange={() => onToggle(plugin)}
          />
        </div>
      </div>
      {expanded && (
        <div className={cn('pb-3 pl-14 pr-3.5', phone && 'pr-4 pl-4')} id={listId}>
          <ul className="space-y-2">
            {own.map((skill) => (
              <li key={skill.path}>
                <span className="block font-mono text-xs">{skill.name}</span>
                <span className="line-clamp-2 text-muted-foreground text-xs">
                  {skill.shortDescription ?? skill.description}
                </span>
              </li>
            ))}
            {duplicates.map(({ skill, plugin: provider }) => (
              <li className="flex items-center gap-2" key={skill.path}>
                <span className="font-mono text-muted-foreground text-xs">{skill.name}</span>
                <Badge size="sm" variant="outline">
                  also in {provider}
                </Badge>
              </li>
            ))}
          </ul>
          {duplicates.length > 0 && (
            <p className="mt-2 text-muted-foreground text-xs">
              Crowdex’s agents use the plugin’s copy of a skill marked “also in”.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button className={settingButtonClass()} onClick={() => onOpen(plugin)} type="button">
              Details
            </button>
            {removable && (
              <button
                className={settingButtonClass()}
                disabled={busy}
                onClick={() => setConfirming(true)}
                type="button"
              >
                Remove from this computer…
              </button>
            )}
          </div>
        </div>
      )}
      {removable && (
        <RemoveMachineSkillsDialog
          onConfirm={async () => {
            await onRemove(plugin);
            setConfirming(false);
          }}
          onOpenChange={setConfirming}
          open={confirming}
          plugin={plugin}
        />
      )}
    </div>
  );
}

/** Confirms deleting a group's folders, naming each, and that other agents lose them too. */
function RemoveMachineSkillsDialog({
  plugin,
  open,
  onOpenChange,
  onConfirm,
}: {
  plugin: MachineSkillPlugin;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
}) {
  const [removing, setRemoving] = useState(false);
  const group = plugin.machineSkills;
  const count = group.folders.length;
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {plugin.displayName} from this computer?</DialogTitle>
          <DialogDescription>
            {count === 1 ? 'This folder' : `These ${count} folders`} will be deleted from{' '}
            {group.label}.{' '}
            {group.shared
              ? `Other agents on this computer, like Codex, use ${count === 1 ? 'it' : 'them'} too and will no longer have ${count === 1 ? 'it' : 'them'}.`
              : `Every Crowdex on this computer will no longer have ${count === 1 ? 'it' : 'them'}.`}
          </DialogDescription>
        </DialogHeader>
        <ul
          aria-label="Folders to delete"
          className="max-h-56 space-y-1 overflow-y-auto rounded-lg bg-control px-3 py-2"
        >
          {group.folders.map((folder) => (
            <li className="truncate font-mono text-xs" key={folder}>
              {folderLabel(group, folder)}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <button
            className={settingButtonClass()}
            onClick={() => onOpenChange(false)}
            type="button"
          >
            Cancel
          </button>
          <Button
            disabled={removing}
            onClick={() => {
              setRemoving(true);
              void onConfirm().finally(() => setRemoving(false));
            }}
            size="sm"
            variant="destructive"
          >
            {removing && <LoaderCircle className="size-3.5 animate-spin" />}
            Remove from this computer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
